import { useEffect } from "react"
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom"

import useRedirectOnBeIdChange from '../../../hooks/useRedirectOnBeIdChange.js'
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Row from 'react-bootstrap/Row'
import Table from 'react-bootstrap/Table'

import AdministrationTabs from '../components/AdministrationTabs.jsx'
import Icon from "../../../components/Icon.jsx"
import Loadable from "../../../components/Loadable.jsx"
import PageTitle from "../../../components/PageTitle.jsx"
import { NotificationTypes, addNotification } from "../../../state/actions/notificationActions"

import { businessEntitiesLoadedSignal, businessEntitiesErrorSignal, businessEntityDictionarySignal } from "../../../state/signals/businessEntitySignals.js"
import { userSignal, userLoadedSignal, userErrorSignal, userPermissionsSignal, userPermissionsLoadedSignal, userPermissionsErrorSignal } from "../../../state/signals/userSignals.js"
import { fetchUser, fetchUserPermissions } from "../../../state/actions/userActions.js"


export default function EditUserPermissions() {

    const navigate = useNavigate()
    const { beId, userId } = useParams()
    useRedirectOnBeIdChange(beId, `/be/${beId}/management/users`)
    const { userPermissionsSummary, config, accessToken, debug, accordionData } = useOutletContext()
    const pageTabSignal = useSignal("permissions")

    useEffect(() => {
        fetchUser(userId, config, accessToken)
        fetchUserPermissions(userId, config, accessToken)
    }, [userId, config, accessToken])

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewUsers) {
            addNotification('You are not authorized to view users for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditUsers
    })

    function PermissionTables({ userPermissions }) {
        if (userPermissions.length == 0) {
            return <div>No permissions to display</div>
        }
        let divs = []
        for (let i = 0; i < userPermissions.length; i++) {
            const item = userPermissions[i]
            const applicationGroupPermissions = item.applicationGroupPermissions ?? []
            const businessEntityPermissions = applicationGroupPermissions.flatMap(group => group.businessEntityPermissions.toSorted((a,b) => a.name.localeCompare(b.name))) ?? []

            let content
            if (businessEntityPermissions.length > 0) {
                content = (
                    <Table responsive striped hover size="sm" className="cc-table">
                        <thead>
                            <tr>
                                <th>Permission Name</th>
                                <th>Application</th>
                                <th>View</th>
                                <th>Enabled</th>
                                <th>Edit</th>
                                <th>Grant</th>
                            </tr>
                        </thead>
                        <tbody>
                            <PermissionTableRows items={businessEntityPermissions} />
                        </tbody>
                    </Table>
                )
            } else {
                content = <>No permissions to display for this application group.</>
            }
            let name
            let key
            if ('businessEntityId' in item) {
                if (item.businessEntityId in businessEntityDictionarySignal.value) {
                    const businessEntity = businessEntityDictionarySignal.value[item.businessEntityId]
                    name = businessEntity.name
                } else {
                    continue; // this business entity is no longer in our list so skp displaying user permissions for it
                }
                key = item.businessEntityId
            } else {
                name = "Error"
                key = `error-${i}`
            }
            divs.push(
                <div key={key} className="mx-3">
                    <h4>{name}</h4>
                    {content}
                </div>
            )
        }
        return divs
    }

    function Checkmark(){
        return <Icon icon="check2" height={16} width={16} />
    }

    function X(){
        return <Icon icon="x" height={16} width={16} />
    }

    function PermissionTableRows({ items }) {
        if (!items || !items.length) {
            return <tr><td colSpan="5" className="text-center">No permissions to display for this business entity.</td></tr>
        }
        let rows = []
        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (!('id' in item)) {
                throw new Error("Undefined permission ID")
            }
            const idx = item.id
            rows.push(
                <tr key={idx}>
                    <td>{item.name}</td>
                    <td>{item.applicationGroupId == 1 ? 'User Management' : 'Command Center'}</td>
                    {
                        item.view === true ? (
                            <td style={{color:"green"}}><Checkmark />&nbsp;View</td>
                        ) : (
                            <td style={{color:"red"}}><X /></td>
                        )
                    }
                    {
                        item.enabled === true ? (
                            <td style={{color:"green"}}><Checkmark />&nbsp;Enabled</td>
                        ) : (
                            <td style={{color:"red"}}><X /></td>
                        )
                    }
                    {
                        item.edit === true ? (
                            <td style={{color:"green"}}><Checkmark />&nbsp;Edit</td>
                        ) : (
                            <td style={{color:"red"}}><X /></td>
                        )
                    }
                    {
                        item.grant === true ? (
                            <td style={{color:"green"}}><Checkmark />&nbsp;Grant</td>
                        ) : (
                            <td style={{color:"red"}}><X /></td>
                        )
                    }
                    {/*Old version*/}
                    {/*
                    <td>{item.enabled === true ? "Enabled" : ""}</td>
                    <td>{item.enabled === true ? "Enabled" : ""}</td>
                    <td>{item.edit === true ? "Edit" : ""}</td>
                    <td>{item.grant === true ? "Grant" : ""}</td>
                    */}
                </tr>
            )
        }
        return <>{rows}</>
    }

    return (
        <>
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/users` }}>Users</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/users/${userId}/` }}>{userSignal.value ? userSignal.value.username : "Loading..."}</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/users/${userId}/permissions` }} active>Permissions</Breadcrumb.Item>
            </Breadcrumb>
            <PageTitle title='User Permissions' canEditSignal={canEditComputed} />
            <AdministrationTabs activeKey="4" />
            <Form id="userdata">
                <Form.Group as={Row} className="mb-3" controlId="UserUsername">
                    <Form.Label column sm="2" className="text-end">Username:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                            <Form.Control name="UserUsername" plaintext readOnly defaultValue={userSignal?.value?.username} />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="UserFirstName">
                    <Form.Label column sm="2" className="text-end">First Name:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                            <Form.Control name="UserFirstName" plaintext readOnly defaultValue={userSignal?.value?.firstName} />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="UserLastName">
                    <Form.Label column sm="2" className="text-end">Last Name:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                            <Form.Control name="UserLastName" plaintext readOnly defaultValue={userSignal?.value?.lastName} />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="UserEmail">
                    <Form.Label column sm="2" className="text-end">Email Address:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                            <Form.Control name="UserEmail" plaintext readOnly defaultValue={userSignal?.value?.email} />
                        </Loadable>
                    </Col>
                </Form.Group>
            </Form>
            <Nav
                variant="tabs"
                size="sm"
                className="px-3 my-3"
                activeKey={pageTabSignal.value}
            >
                <Nav.Item>
                    <Link to={`/be/${beId}/management/users/${userId}`} eventkey="profile" className="nav-link">Profile</Link>
                </Nav.Item>
                <Nav.Item>
                    <Link to={`/be/${beId}/management/users/${userId}/roles`} eventkey="roles" className="nav-link">Roles</Link>
                </Nav.Item>
                <Nav.Item>
                    <Link to={`/be/${beId}/management/users/${userId}/permissions`} eventkey="permissions" className="nav-link active">Permissions</Link>
                </Nav.Item>
            </Nav>
            <Loadable loadedSignals={[businessEntitiesLoadedSignal, userPermissionsLoadedSignal]} errorSignals={[businessEntitiesErrorSignal, userPermissionsErrorSignal]}>
                <PermissionTables userPermissions={userPermissionsSignal.value} />
            </Loadable>
        </>
    )
}